import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApp } from "../context/AppContext";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import Logo from "../components/Logo";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
);
const fmt = (n) => n?.toLocaleString() ?? "\u2014";
const fmtM = (n) => `$${(n || 0).toFixed(2)}`;
const fmtMoney = (amount, currency = "USD") => {
  const symbol = currency === "USD" ? "$" : currency === "SAR" ? "\uFDFC" : currency === "EUR" ? "\u20AC" : currency === "GBP" ? "\xA3" : currency;
  return currency === "USD" ? `${symbol}${(amount || 0).toFixed(2)}` : `${(amount || 0).toFixed(2)} ${symbol}`;
};
const fmtD = (d) => new Date(d).toLocaleDateString(void 0, { month: "short", day: "numeric", year: "numeric" });
const fmtT = (d) => new Date(d).toLocaleString(void 0, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const fmtK = (n) => n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : String(n || 0);
const T = {
  accent: "#3dba6e",
  accentDim: "rgba(61,186,110,.15)",
  purple: "#a78bfa",
  blue: "#38bdf8",
  amber: "#fbbf24",
  red: "#f87171",
  orange: "#fb923c",
  bg: "var(--bg-base)",
  bgCard: "var(--bg-card)",
  bgSide: "var(--bg-sidebar)",
  border: "var(--border)",
  border2: "var(--border2)",
  text1: "var(--text-1)",
  text2: "var(--text-2)",
  text3: "var(--text-3)"
};
// Inline line-icon set (no external libs). All icons inherit currentColor and
// use a consistent 1.7 stroke. <Icon name="..." /> replaces every emoji.
const ICON_PATHS = {
  overview: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
  users: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M16 5.2a3 3 0 0 1 0 5.6" /><path d="M17.5 20a5 5 0 0 0-3-4.6" /></>,
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></>,
  orders: <><path d="M6 7h12l-1 12a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1L6 7Z" /><path d="M9 7a3 3 0 0 1 6 0" /><path d="M9.5 11v0M14.5 11v0" /></>,
  sessions: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  tokens: <><circle cx="12" cy="12" r="9" /><path d="M8.5 12h7M12 8.5v7" /></>,
  openai: <><circle cx="12" cy="12" r="9" /><path d="M12 3a9 9 0 0 0 0 18M3 12h18" /></>,
  deletions: <><path d="M4 7h16" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /><path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" /><path d="M10 11v6M14 11v6" /></>,
  revenue: <><path d="M4 19V5" /><path d="M4 19h16" /><path d="M8 16l3.5-4 3 2.5L20 8" /></>,
  money: <><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.2a2.4 2.4 0 0 1 2.5-1.7c1.3 0 2.4.8 2.4 1.9 0 2.6-4.8 1.4-4.8 4 0 1.1 1.1 1.9 2.4 1.9a2.4 2.4 0 0 0 2.5-1.7" /></>,
  wallet: <><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10h18" /><circle cx="16.5" cy="14" r="1.3" /></>,
  card: <><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 9.5h19" /><path d="M6 15h4" /></>,
  chart: <><path d="M4 20V4" /><path d="M4 20h16" /><rect x="7" y="12" width="3" height="5" rx="1" /><rect x="12" y="8" width="3" height="9" rx="1" /><rect x="17" y="14" width="3" height="3" rx="1" /></>,
  trendUp: <><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></>,
  bulb: <><path d="M9 18h6" /><path d="M10 21h4" /><path d="M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.3 1 2.5h6c0-1.2.3-1.8 1-2.5A6 6 0 0 0 12 3Z" /></>,
  message: <><path d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" /></>,
  bolt: <><path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13l0-8Z" /></>,
  crown: <><path d="M4 8l3.5 3L12 5l4.5 6L20 8l-1.5 10h-13L4 8Z" /><path d="M5.5 21h13" /></>,
  live: <><circle cx="12" cy="12" r="3" /><path d="M6.5 6.5a8 8 0 0 0 0 11M17.5 6.5a8 8 0 0 1 0 11M4 4a12 12 0 0 0 0 16M20 4a12 12 0 0 1 0 16" /></>,
  sunrise: <><path d="M12 3v5M5 12H3M21 12h-2M6 7l-1.5-1.5M18 7l1.5-1.5" /><path d="M8 12a4 4 0 0 1 8 0" /><path d="M2 16h20M6 20h12" /></>,
  trophy: <><path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" /><path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" /><path d="M12 13v4M8.5 21h7M10 17h4l.5 4h-5l.5-4Z" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="16" rx="2" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /></>,
  robot: <><rect x="4.5" y="8" width="15" height="11" rx="2.5" /><path d="M12 4v4M12 4h-1.5M12 4h1.5" /><circle cx="9" cy="13" r="1.1" /><circle cx="15" cy="13" r="1.1" /><path d="M9.5 16.5h5" /></>,
  lock: <><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  refresh: <><path d="M20 11a8 8 0 0 0-14-4.5L4 8" /><path d="M4 4v4h4" /><path d="M4 13a8 8 0 0 0 14 4.5L20 16" /><path d="M20 20v-4h-4" /></>,
  moon: <><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
  logout: <><path d="M15 5V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" /><path d="M10 12h11M18 9l3 3-3 3" /></>,
  collapse: <><path d="M9 6l-6 6 6 6M21 6l-6 6 6 6" /></>,
  warning: <><path d="M12 3.5 22 20H2L12 3.5Z" /><path d="M12 10v4.5M12 17.5h.01" /></>,
  empty: <><circle cx="12" cy="12" r="9" /><path d="M8 15c1-1.2 2.4-2 4-2s3 .8 4 2M9 9.5h.01M15 9.5h.01" /></>,
};
const Icon = ({ name, size = 18, style, strokeWidth = 1.7 }) => {
  const p = ICON_PATHS[name];
  if (!p) return null;
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: "block", ...style }} aria-hidden="true">{p}</svg>;
};
const CHART_DEFAULTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: {} } },
  scales: {
    x: { grid: { color: "rgba(128,128,128,.12)" }, ticks: { color: "#8a8f98", font: { family: "Outfit", size: 11 } } },
    y: { grid: { color: "rgba(128,128,128,.12)" }, ticks: { color: "#8a8f98", font: { family: "Outfit", size: 11 } } }
  }
};
const Avatar = ({ src, name, size = 28 }) => src ? <img src={src} alt="" style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} /> : <div style={{ width: size, height: size, borderRadius: "50%", background: `${T.accent}22`, border: `1px solid ${T.accent}44`, display: "flex", alignItems: "center", justifyContent: "center", color: T.accent, fontSize: size * 0.38, fontWeight: 700, flexShrink: 0, fontFamily: "Outfit,sans-serif" }}>
      {name?.[0]?.toUpperCase() || "?"}
    </div>;
const Badge = ({ children, color = T.accent }) => <span style={{ background: `${color}18`, color, border: `1px solid ${color}30`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600, fontFamily: "Outfit,sans-serif", whiteSpace: "nowrap" }}>
    {children}
  </span>;
const SectionTitle = ({ children, icon }) => <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "32px 0 14px", paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
    {icon && <span style={{ color: T.accent, display: "flex" }}><Icon name={icon} size={16} /></span>}
    <h3 style={{ fontFamily: "Outfit,sans-serif", fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: 2, margin: 0 }}>
      {children}
    </h3>
  </div>;
const KpiCard = ({ icon, label, value, sub, color = T.accent, trend, onClick }) => <div
  onClick={onClick}
  style={{
    background: T.bgCard,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    padding: "20px 22px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    cursor: onClick ? "pointer" : "default",
    transition: "border-color .15s, transform .15s",
    position: "relative",
    overflow: "hidden"
  }}
  onMouseEnter={(e) => {
    if (onClick) {
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.transform = "translateY(-1px)";
    }
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.borderColor = "";
    e.currentTarget.style.transform = "";
  }}
>
    {
  /* colored top bar */
}
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: color, borderRadius: "16px 16px 0 0", opacity: 0.7 }} />
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: `${color}18`, color, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name={icon} size={20} /></div>
      {trend !== void 0 && <span style={{ fontSize: 12, fontWeight: 600, color: trend >= 0 ? T.accent : T.red, fontFamily: "Outfit,sans-serif" }}>
          {trend >= 0 ? "\u2191" : "\u2193"} {Math.abs(trend)}%
        </span>}
    </div>
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, fontFamily: "Outfit,sans-serif", color: T.text1, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
      <div style={{ fontSize: 12, color: T.text3, marginTop: 4, fontFamily: "Outfit,sans-serif" }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color, marginTop: 3, fontFamily: "Outfit,sans-serif" }}>{sub}</div>}
    </div>
  </div>;
const ChartCard = ({ title, icon, height = 220, children }) => <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, padding: "18px 20px" }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: T.text2, marginBottom: 14, fontFamily: "Outfit,sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
      {icon && <span style={{ color: T.accent, display: "flex" }}><Icon name={icon} size={15} /></span>}{title}
    </div>
    <div style={{ height, position: "relative" }}>{children}</div>
  </div>;
const ChartLegend = ({ items }) => <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10 }}>
    {items.map(({ color, label }) => <span key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.text3, fontFamily: "Outfit,sans-serif" }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />{label}
      </span>)}
  </div>;
const DataTable = ({ headers, children, emptyMsg = "No data", emptyColSpan }) => <div className="adm-table-wrap" style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, overflowX: "auto", overflowY: "hidden" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 760 }}>
      <thead>
        <tr style={{ background: T.bgSide, borderBottom: `1px solid ${T.border}` }}>
          {headers.map((h) => <th key={h} style={{ padding: "11px 16px", textAlign: "left", color: T.text3, fontWeight: 600, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1, fontFamily: "Outfit,sans-serif" }}>{h}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    {!children?.length && <div style={{ padding: 40, textAlign: "center", color: T.text3, fontSize: 13, fontFamily: "Outfit,sans-serif" }}>{emptyMsg}</div>}
  </div>;
const TR = ({ children, i }) => <tr
  style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 ? "rgba(128,128,128,.04)" : "transparent", transition: "background .1s" }}
  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(61,186,110,.08)"}
  onMouseLeave={(e) => e.currentTarget.style.background = i % 2 ? "rgba(128,128,128,.04)" : "transparent"}
>{children}</tr>;
const TD = ({ children, style }) => <td style={{ padding: "11px 16px", fontFamily: "Outfit,sans-serif", ...style }}>{children}</td>;
const Btn = ({ children, color = T.text2, border = T.border2, onClick, disabled }) => <button
  disabled={disabled}
  onClick={onClick}
  style={{ padding: "4px 11px", borderRadius: 7, border: `1px solid ${border}`, background: "transparent", color, cursor: "pointer", fontSize: 11, fontFamily: "Outfit,sans-serif", opacity: disabled ? 0.5 : 1, transition: "background .1s" }}
  onMouseEnter={(e) => !disabled && (e.currentTarget.style.background = "rgba(255,255,255,.06)")}
  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
>{children}</button>;
const Toast = ({ toast }) => toast ? <div style={{
  position: "fixed",
  top: 20,
  right: 20,
  zIndex: 9999,
  background: toast.type === "error" ? "#7f1d1d" : "#14532d",
  color: "#fff",
  borderRadius: 12,
  padding: "11px 20px",
  fontSize: 13,
  fontWeight: 600,
  boxShadow: "0 8px 32px rgba(0,0,0,.5)",
  fontFamily: "Outfit,sans-serif",
  display: "flex",
  alignItems: "center",
  gap: 8,
  animation: "slideIn .2s ease"
}}>
    <span>{toast.type === "error" ? "\u26A0\uFE0F" : "\u2713"}</span>{toast.msg}
  </div> : null;
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { language, toggleLanguage, theme, toggleTheme } = useApp();
  const isAr = language === "ar";
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tokenStats, setTokenStats] = useState(null);
  const [openaiUsage, setOpenaiUsage] = useState(null);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletionActionLoading, setDeletionActionLoading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userPages, setUserPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [manageUser, setManageUser] = useState(null); // full detail object for the modal
  const [manageLoading, setManageLoading] = useState(false);
  const [live, setLive] = useState(null);
  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close the mobile drawer whenever a tab is chosen.
  useEffect(() => { setMobileNavOpen(false); }, [tab]);
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3e3);
  };
  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get("/api/admin/stats");
      setStats(r.data);
    } catch {
      navigate("/signin");
    }
  }, [navigate]);
  const loadUsers = useCallback(async (page = 1, q = "") => {
    try {
      const r = await axios.get(`/api/admin/users?page=${page}&limit=15&search=${q}`);
      setUsers(r.data.users);
      setTotalUsers(r.data.total);
      setUserPages(r.data.pages);
    } catch {
    }
  }, []);
  const loadOrders = useCallback(async () => {
    try {
      const r = await axios.get("/api/admin/orders?limit=20");
      setOrders(r.data.orders);
    } catch {
    }
  }, []);
  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get("/api/admin/active-sessions");
      setSessions(r.data.sessions);
    } catch {
    }
  }, []);
  const loadTokens = useCallback(async () => {
    try {
      // Correct endpoint is /token-stats (the old /tokens 404'd).
      const { data } = await axios.get("/api/admin/token-stats");
      setTokenStats(data);
    } catch (err) {
      console.error("Token load fail", err);
    }
  }, []);
  const loadOpenAiData = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/admin/openai/usage");
      setOpenaiUsage(data);
    } catch (err) {
      console.error("OpenAI load fail", err);
    }
  }, []);
  const loadDeletionRequests = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/admin/deletion-requests");
      setDeletionRequests(data.requests || []);
    } catch (err) {
      console.error("Deletion requests load fail", err);
    }
  }, []);
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadUsers(), loadOrders(), loadSessions(), loadTokens(), loadOpenAiData(), loadDeletionRequests()]);
      setLoading(false);
    })();
    const iv = setInterval(loadOpenAiData, 6e4);
    return () => clearInterval(iv);
  }, [loadUsers, loadOrders, loadSessions, loadStats, loadTokens, loadOpenAiData, loadDeletionRequests]);
  const approveDeletion = async (userId) => {
    if (!window.confirm(t("adm_deletion_approve_confirm"))) return;
    setDeletionActionLoading(userId);
    try {
      await axios.post(`/api/admin/deletion-requests/${userId}/approve`);
      setDeletionRequests((p) => p.filter((u) => u._id !== userId));
      setUsers((p) => p.filter((u) => u._id !== userId));
      setTotalUsers((t2) => Math.max(0, t2 - 1));
      showToast(t("adm_delete_success") || "Account deleted");
    } catch {
      showToast(t("error"), "error");
    } finally {
      setDeletionActionLoading(null);
    }
  };
  const rejectDeletion = async (userId) => {
    if (!window.confirm(t("adm_deletion_reject_confirm"))) return;
    setDeletionActionLoading(userId);
    try {
      await axios.post(`/api/admin/deletion-requests/${userId}/reject`);
      setDeletionRequests((p) => p.filter((u) => u._id !== userId));
      showToast("Request rejected");
    } catch {
      showToast(t("error"), "error");
    } finally {
      setDeletionActionLoading(null);
    }
  };
  useEffect(() => {
    loadUsers(userPage, search);
  }, [userPage, search, loadUsers]);
  const handleBan = async (userId, banned) => {
    setActionLoading(userId);
    try {
      await axios.patch(`/api/admin/users/${userId}`, { banned });
      setUsers((p) => p.map((u) => u._id === userId ? { ...u, banned } : u));
      showToast(banned ? "User banned" : "User unbanned");
    } catch {
      showToast("Action failed", "error");
    } finally {
      setActionLoading(null);
    }
  };
  const handleDelete = async (userId) => {
    if (!window.confirm(t("adm_confirm_delete_user"))) return;
    setActionLoading(userId);
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setUsers((p) => p.filter((u) => u._id !== userId));
      setTotalUsers((t2) => t2 - 1);
      showToast(t("adm_delete_success") || "User deleted");
    } catch {
      showToast(t("error"), "error");
    } finally {
      setActionLoading(null);
    }
  };
  const handleMakeAdmin = async (userId, role) => {
    const newRole = role === "admin" ? "user" : "admin";
    if (!window.confirm(newRole === "admin" ? isAr ? "\u0647\u0644 \u062A\u0631\u064A\u062F \u062A\u0639\u064A\u064A\u0646 \u0647\u0630\u0627 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0643\u0645\u062F\u064A\u0631\u061F" : "Make this user an Admin?" : isAr ? "\u0625\u0632\u0627\u0644\u0629 \u0635\u0644\u0627\u062D\u064A\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631\u061F" : "Remove admin role?")) return;
    setActionLoading(userId);
    try {
      await axios.patch(`/api/admin/users/${userId}`, { role: newRole });
      setUsers((p) => p.map((u) => u._id === userId ? { ...u, role: newRole } : u));
      showToast(newRole === "admin" ? "\u2B50 Admin role granted" : "Admin role removed");
    } catch {
      showToast(t("error"), "error");
    } finally {
      setActionLoading(null);
    }
  };
  const openManage = async (userId) => {
    setManageLoading(true);
    setManageUser({ loading: true });
    try {
      const { data } = await axios.get(`/api/admin/users/${userId}/detail`);
      setManageUser(data);
    } catch {
      showToast(t("error"), "error");
      setManageUser(null);
    } finally {
      setManageLoading(false);
    }
  };
  const saveUserPlan = async (userId, payload) => {
    setManageLoading(true);
    try {
      const { data } = await axios.patch(`/api/admin/users/${userId}/plan`, payload);
      setManageUser((m) => m ? { ...m, user: { ...m.user, plan: data.plan, messageCount: data.messageCount } } : m);
      setUsers((p) => p.map((u) => u._id === userId ? { ...u, plan: data.plan } : u));
      showToast(isAr ? "تم تحديث الخطة" : "Plan updated");
    } catch {
      showToast(t("error"), "error");
    } finally {
      setManageLoading(false);
    }
  };
  const setOrderStatus = async (orderId, status) => {
    if (!window.confirm(isAr ? `تغيير حالة الطلب إلى ${status}؟` : `Change order status to ${status}?`)) return;
    try {
      await axios.patch(`/api/admin/orders/${orderId}/status`, { status });
      setOrders((p) => p.map((o) => o.orderId === orderId ? { ...o, status } : o));
      showToast(isAr ? "تم تحديث الطلب" : "Order updated");
    } catch {
      showToast(t("error"), "error");
    }
  };
  const loadLive = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/admin/live");
      setLive(data);
    } catch {
    }
  }, []);
  useEffect(() => {
    loadLive();
    const iv = setInterval(loadLive, 15e3);
    return () => clearInterval(iv);
  }, [loadLive]);
  const logout = async () => {
    await axios.post("/api/auth/signout").catch(() => {
    });
    localStorage.removeItem("auth_token");
    navigate("/signin");
  };
  void tokenStats;
  if (loading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg, flexDirection: "column", gap: 20 }}>
      <div style={{ width: 64, height: 64, borderRadius: 20, background: T.accentDim, border: `1px solid ${T.accent}33`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Logo variant="full" size={44} style={{ borderRadius: 0, background: "transparent" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <p style={{ color: T.text1, fontFamily: "Outfit,sans-serif", fontSize: 16, fontWeight: 600, margin: 0 }}>TaweelAI Admin</p>
        <p style={{ color: T.text3, fontFamily: "Outfit,sans-serif", fontSize: 13, margin: 0 }}>Loading dashboard…</p>
      </div>
      <div style={{ width: 180, height: 3, background: "rgba(255,255,255,.06)", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ width: "60%", height: "100%", background: T.accent, borderRadius: 99, animation: "loadBar 1.2s ease-in-out infinite alternate" }} />
      </div>
      <style>{`@keyframes loadBar { from{transform:translateX(-60%)} to{transform:translateX(200%)} } @keyframes slideIn { from{transform:translateX(40px);opacity:0} to{transform:translateX(0);opacity:1} }`}</style>
    </div>;
  const navItems = [
    { id: "overview", icon: "overview", label: t("adm_overview") },
    { id: "users", icon: "users", label: t("adm_users") },
    { id: "orders", icon: "orders", label: t("adm_orders") },
    { id: "sessions", icon: "sessions", label: t("adm_sessions") },
    { id: "tokens", icon: "tokens", label: t("adm_tokens") },
    { id: "openai", icon: "openai", label: t("adm_openai") },
    { id: "deletions", icon: "deletions", label: t("adm_deletion_requests"), badge: deletionRequests.length }
  ];
  const dailyLabels = stats?.charts?.dailySignups?.map((d) => d._id?.slice(5)) || [];
  const signupData = stats?.charts?.dailySignups?.map((d) => d.count) || [];
  const revenueData = stats?.charts?.dailyRevenue?.map((d) => d.total) || [];
  const tokenData = stats?.charts?.dailyTokens?.map((d) => d.tokens) || [];
  const msgData = stats?.charts?.dailyMessages?.map((d) => d.count) || [];
  const providerGoogle = stats?.users?.google || 0;
  const providerEmail = stats?.users?.email || 0;
  const planFree = (stats?.users?.total || 0) - (stats?.users?.paid || 0);
  const planPaid = stats?.users?.paid || 0;
  return <div className="adm-shell" style={{ display: "flex", height: "100vh", overflow: "hidden", background: T.bg, fontFamily: "inherit", direction: "ltr" }}>
      <Toast toast={toast} />
      <style>{`
        @keyframes loadBar { from{transform:translateX(-100%)} to{transform:translateX(200%)} }
        @keyframes slideIn { from{transform:translateX(40px);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.5} }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128,128,128,.28); border-radius:99px; }
        .adm-nav-item:hover { background: var(--bg-hover); }
      `}</style>
      {
    
  }
      {mobileNavOpen && <div className="adm-nav-overlay" onClick={() => setMobileNavOpen(false)} />}
      <nav className={`adm-nav ${mobileNavOpen ? "adm-nav--open" : ""}`} style={{
    width: sideCollapsed ? 64 : 230,
    background: T.bgSide,
    borderRight: `1px solid ${T.border}`,
    display: "flex",
    flexDirection: "column",
    padding: "20px 0",
    flexShrink: 0,
    overflowY: "auto",
    overflowX: "hidden",
    transition: "width .2s ease"
  }}>
        {
    
  }
        <div style={{ padding: sideCollapsed ? "0 10px 16px" : "0 18px 16px", borderBottom: `1px solid ${T.border}`, marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.accentDim, border: `1px solid ${T.accent}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 6 }}>
            <Logo variant="mark" size={40} style={{ borderRadius: 0, background: "transparent", width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
          {!sideCollapsed && <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text1, fontFamily: "inherit", lineHeight: 1 }}>TaweelAI</div>
              <div style={{ fontSize: 10, color: T.accent, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginTop: 2 }}>{t("admin")}</div>
            </div>}
        </div>
        {
    
  }
        {navItems.map((item) => <button key={item.id} title={sideCollapsed ? item.label : ""} onClick={() => setTab(item.id)} style={{
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: sideCollapsed ? "10px 0" : "10px 18px",
    justifyContent: sideCollapsed ? "center" : "flex-start",
    border: "none",
    cursor: "pointer",
    fontFamily: "Outfit,sans-serif",
    fontSize: 13,
    fontWeight: tab === item.id ? 700 : 400,
    background: tab === item.id ? T.accentDim : "transparent",
    color: tab === item.id ? T.accent : T.text2,
    borderLeft: tab === item.id && !sideCollapsed ? `3px solid ${T.accent}` : "3px solid transparent",
    transition: "all .15s",
    width: "100%",
    position: "relative"
  }}>
            <span style={{ flexShrink: 0, display: "flex" }}><Icon name={item.icon} size={19} /></span>
            {!sideCollapsed && item.label}
            {item.badge > 0 && <span style={{ marginInlineStart: "auto", background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, borderRadius: 999, padding: sideCollapsed ? "0" : "2px 7px", minWidth: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", position: sideCollapsed ? "absolute" : "static", top: sideCollapsed ? 6 : "auto", right: sideCollapsed ? 8 : "auto" }}>{item.badge}</span>}
          </button>)}
        <div style={{ flex: 1 }} />
        {
    
  }
        <button onClick={() => setSideCollapsed((c) => !c)} style={{
    margin: "0 0 4px",
    padding: "10px 18px",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    color: T.text3,
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: sideCollapsed ? "center" : "flex-start",
    gap: 8
  }}>
          <span style={{ display: "inline-block", transform: sideCollapsed ? isAr ? "rotate(180deg)" : "rotate(0deg)" : isAr ? "rotate(0deg)" : "rotate(180deg)", transition: "transform .2s" }}>{isAr ? "\u25B6" : "\u25C0"}</span>
          {!sideCollapsed && t("adm_collapse")}
        </button>
        {
    
  }
        <button onClick={() => toggleLanguage(isAr ? "en" : "ar")} style={{
    padding: "10px 18px",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 13,
    color: T.text2,
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: sideCollapsed ? "center" : "flex-start",
    gap: 10,
    transition: "all .15s"
  }}>
          <Icon name="globe" size={16} />
          {!sideCollapsed && (isAr ? "English" : "\u0627\u0644\u0639\u0631\u0628\u064A\u0629")}
        </button>
        <button onClick={toggleTheme} title={theme === "dark" ? "Light mode" : "Dark mode"} style={{
    padding: "10px 18px", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13,
    color: T.text2, background: "transparent", display: "flex", alignItems: "center",
    justifyContent: sideCollapsed ? "center" : "flex-start", gap: 10, transition: "all .15s"
  }}>
          <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
          {!sideCollapsed && (theme === "dark" ? (isAr ? "\u0627\u0644\u0648\u0636\u0639 \u0627\u0644\u0641\u0627\u062A\u062D" : "Light mode") : (isAr ? "\u0627\u0644\u0648\u0636\u0639 \u0627\u0644\u062F\u0627\u0643\u0646" : "Dark mode"))}
        </button>
        <button onClick={logout} style={{
    display: "flex",
    alignItems: "center",
    justifyContent: sideCollapsed ? "center" : "flex-start",
    gap: 8,
    padding: sideCollapsed ? "10px 0" : "10px 18px",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 13,
    color: T.red,
    background: "transparent"
  }}>
          <Icon name="logout" size={16} />{!sideCollapsed && t("adm_logout")}
        </button>
      </nav>
      {
    
  }
      <main className="adm-main" style={{ flex: 1, overflowY: "auto", padding: "28px 32px" }}>
        <button className="adm-hamburger" onClick={() => setMobileNavOpen(true)} aria-label="Menu" style={{
    display: "none", alignItems: "center", justifyContent: "center", gap: 8,
    marginBottom: 16, padding: "10px 14px", background: T.bgCard, border: `1px solid ${T.border2}`,
    borderRadius: 12, color: T.text1, cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 600
  }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          {isAr ? "القائمة" : "Menu"}
        </button>
        {
    
  }
        {tab === "overview" && stats && <>
            {
    
  }
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
              <div>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text1, margin: 0, letterSpacing: "-0.5px" }}>{t("adm_dashboard")}</h2>
                <p style={{ fontSize: 13, color: T.text3, margin: "4px 0 0" }}>{t("adm_platform_ov")} · {t("adm_last_7_days")}</p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 14px", fontSize: 12, color: T.text3, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", display: "inline-block", boxShadow: "0 0 0 3px rgba(34,197,94,.18)" }} /> <span style={{ color: T.accent, fontWeight: 600 }}>{t("adm_live")}</span>
                </div>
                <button onClick={() => Promise.all([loadStats(), loadTokens(), loadOpenAiData(), loadLive()])} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 14px", fontSize: 12, color: T.text2, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="refresh" size={14} /> {t("adm_refresh")}
                </button>
              </div>
            </div>
            {live && <>
              <SectionTitle icon="live">{isAr ? "المراقبة المباشرة" : "Live monitoring"}</SectionTitle>
              <div className="adm-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 12, marginBottom: 20 }}>
                <KpiCard icon="live" label={isAr ? "متصل الآن" : "Online now"} value={fmt(live.onlineNow)} color="#22c55e" />
                <KpiCard icon="bolt" label={isAr ? "جلسات بالساعة" : "Active hourly"} value={fmt(live.activeHourly)} color={T.purple} />
                <KpiCard icon="crown" label={isAr ? "اشتراكات شهرية" : "Active monthly"} value={fmt(live.activeMonthly)} color={T.blue} />
                <KpiCard icon="message" label={isAr ? "رسائل اليوم" : "Messages today"} value={fmt(live.todayMessages)} color={T.amber} />
                <KpiCard icon="money" label={isAr ? "إيراد اليوم" : "Revenue today"} value={fmtM(live.todayRevenue)} color={T.accent} />
              </div>
              {live.recentActivity?.length > 0 && <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text3, marginBottom: 10 }}>{isAr ? "آخر النشاط (5 دقائق)" : "Recent activity (last 5 min)"}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 200, overflowY: "auto" }}>
                  {live.recentActivity.map((a) => <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
                    <span style={{ color: a.role === "assistant" ? T.accent : T.blue, display: "flex" }}><Icon name={a.role === "assistant" ? "robot" : "user"} size={15} /></span>
                    <span style={{ color: T.text2, fontWeight: 600, minWidth: 120 }}>{a.user?.name || (isAr ? "زائر" : "Guest")}</span>
                    <span style={{ color: T.text3, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} dir="auto">{a.preview}</span>
                    <span style={{ color: T.text3, fontSize: 10 }}>{fmtT(a.at)}</span>
                  </div>)}
                </div>
              </div>}
            </>}
            <SectionTitle icon="users">{t("adm_users")}</SectionTitle>
            <div className="adm-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 12 }}>
              <KpiCard icon="users" label={t("adm_total_users")} value={fmtK(stats.users.total)} sub={`${stats.users.today >= 0 ? "+" : ""}${stats.users.today} ${t("adm_new_today").toLowerCase()}`} color={T.accent} trend={stats.users.weekTrend} />
              <KpiCard icon="trendUp" label={t("adm_new_this_week")} value={fmtK(stats.users.week)} color={T.purple} />
              <KpiCard icon="tokens" label={t("adm_all_time_tokens")} value={fmtK(stats.tokens?.total)} color={T.blue} />
              <KpiCard icon="bulb" label={t("adm_est_api_cost")} value={`$${((stats.tokens?.total || 0) * 15e-8).toFixed(2)}`} color={T.amber} />
            </div>
            {
    
  }
            <div className="adm-two-col" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginTop: 16 }}>
              <ChartCard title={t("adm_signups_chart")} icon="trendUp" height={200}>
                <Bar
    data={{
      labels: dailyLabels,
      datasets: [{
        label: t("adm_users"),
        data: signupData,
        backgroundColor: "rgba(61,186,110,.5)",
        borderColor: "#3dba6e",
        borderWidth: 1.5,
        borderRadius: 5
      }]
    }}
    options={{ ...CHART_DEFAULTS, plugins: { ...CHART_DEFAULTS.plugins, tooltip: { callbacks: { label: (c) => ` ${c.parsed.y} ${t("adm_users").toLowerCase()}` } } } }}
  />
              </ChartCard>
              <ChartCard title={t("adm_auth_split")} icon="lock" height={200}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12 }}>
                  <div style={{ height: 140, width: 140, position: "relative" }}>
                    <Doughnut
    data={{
      labels: [t("adm_google_oauth"), t("adm_email_signups")],
      datasets: [{
        data: [providerGoogle, providerEmail],
        backgroundColor: ["#4285F4", T.orange],
        borderWidth: 0,
        hoverOffset: 4
      }]
    }}
    options={{ ...CHART_DEFAULTS, cutout: "68%", plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => ` ${c.label}: ${c.raw}` } } } }}
  />
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 17, fontWeight: 800, color: T.text1, fontFamily: "inherit" }}>{fmtK(stats.users.total)}</span>
                      <span style={{ fontSize: 10, color: T.text3 }}>{t("adm_total")}</span>
                    </div>
                  </div>
                  <ChartLegend items={[{ color: "#4285F4", label: `${t("adm_google_oauth")} (${providerGoogle})` }, { color: T.orange, label: `${t("adm_email_signups")} (${providerEmail})` }]} />
                </div>
              </ChartCard>
            </div>
            {
    
  }
            <SectionTitle icon="message">{isAr ? "\u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0627\u062A" : "Conversations"}</SectionTitle>
            <div className="adm-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 12 }}>
              <KpiCard icon="message" label={t("adm_total_sessions")} value={fmtK(stats.chats.total)} sub={`${stats.chats.today} ${t("adm_today").toLowerCase()}`} color={T.accent} />
              <KpiCard icon="message" label={t("adm_total_messages")} value={fmtK(stats.chats.messages)} color={T.blue} />
              <KpiCard icon="bolt" label={t("adm_active_now")} value={fmtK(stats.sessions.activeHourly)} color={T.amber} />
              <KpiCard icon="chart" label={t("adm_avg_msgs_session")} value={stats.chats.total ? Math.round(stats.chats.messages / stats.chats.total) : 0} color={T.purple} />
            </div>
            {
    
  }
            {msgData.length > 0 && <div style={{ marginTop: 16 }}>
                <ChartCard title={t("adm_daily_msgs_chart")} icon="message" height={180}>
                  <Line
    data={{
      labels: dailyLabels,
      datasets: [{
        label: t("adm_total_messages"),
        data: msgData,
        borderColor: T.blue,
        backgroundColor: "rgba(56,189,248,.1)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: T.blue,
        borderWidth: 2
      }]
    }}
    options={{ ...CHART_DEFAULTS, plugins: { ...CHART_DEFAULTS.plugins, tooltip: { callbacks: { label: (c) => ` ${c.parsed.y} ${isAr ? "\u0631\u0633\u0627\u0626\u0644" : "msgs"}` } } } }}
  />
                </ChartCard>
              </div>}
            {
    
  }
            <SectionTitle icon="tokens">{t("adm_token_usage")}</SectionTitle>
            <div className="adm-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 12 }}>
              <KpiCard icon="tokens" label={t("adm_all_time_tokens")} value={fmtK(stats.tokens?.total)} color={T.blue} />
              <KpiCard icon="sunrise" label={t("adm_tokens_today")} value={fmtK(stats.tokens?.today)} sub={isAr ? "\u0639\u0628\u0631 \u062C\u0645\u064A\u0639 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u064A\u0646" : "across all users"} color={T.purple} />
              <KpiCard icon="bulb" label={t("adm_est_api_cost")} value={`$${((stats.tokens?.total || 0) * 15e-8).toFixed(2)}`} color={T.amber} />
            </div>
            <div className="adm-two-col" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginTop: 16 }}>
              <ChartCard title={t("adm_token_consump_chart")} icon="tokens" height={200}>
                <Bar
    data={{
      labels: dailyLabels,
      datasets: [{
        label: isAr ? "\u062A\u0648\u0643\u0646\u0632" : "Tokens",
        data: tokenData,
        backgroundColor: "rgba(56,189,248,.45)",
        borderColor: T.blue,
        borderWidth: 1.5,
        borderRadius: 5
      }]
    }}
    options={{ ...CHART_DEFAULTS, plugins: { ...CHART_DEFAULTS.plugins, tooltip: { callbacks: { label: (c) => ` ${fmtK(c.parsed.y)} ${isAr ? "\u062A\u0648\u0643\u0646\u0632" : "tokens"}` } } } }}
  />
              </ChartCard>
              <ChartCard title={t("adm_top_token_consumers")} icon="trophy" height={200}>
                <div style={{ overflowY: "auto", maxHeight: 200 }}>
                  {(stats.topTokenUsers || []).map((u, i) => <div key={u._id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 11, color: T.text3, width: 18, textAlign: "right", flexShrink: 0 }}>#{i + 1}</span>
                      <Avatar src={u.avatar} name={u.name} size={24} />
                      <span style={{ flex: 1, fontSize: 12, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</span>
                      <span style={{ fontSize: 12, color: T.blue, fontWeight: 700 }}>{fmtK(u.totalTokensUsed)}</span>
                    </div>)}
                </div>
              </ChartCard>
            </div>
            {
    
  }
            <SectionTitle icon="card">{isAr ? "\u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A" : "Revenue"}</SectionTitle>
            <div className="adm-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 12 }}>
              <KpiCard icon="money" label={t("adm_all_time_revenue")} value={fmtM(stats.revenue.total)} sub={`${stats.orders.total} ${t("adm_orders").toLowerCase()}`} color={T.accent} />
              <KpiCard icon="calendar" label={t("adm_this_month")} value={fmtM(stats.revenue.month)} sub={`${stats.orders.month} ${t("adm_orders").toLowerCase()}`} color={T.purple} />
              <KpiCard icon="sunrise" label={t("adm_today")} value={fmtM(stats.revenue.today)} sub={`${stats.orders.today} ${t("adm_orders").toLowerCase()}`} color="#34d399" />
              <KpiCard icon="money" label={t("adm_avg_order_value")} value={stats.orders.total ? fmtM(stats.revenue.total / stats.orders.total) : "$0.00"} color={T.amber} />
            </div>
            <div className="adm-two-col" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginTop: 16, marginBottom: 32 }}>
              <ChartCard title={t("adm_revenue_chart")} icon="money" height={200}>
                <Bar
    data={{
      labels: dailyLabels,
      datasets: [{
        label: isAr ? "\u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A" : "Revenue",
        data: revenueData,
        backgroundColor: "rgba(167,139,250,.45)",
        borderColor: T.purple,
        borderWidth: 1.5,
        borderRadius: 5
      }]
    }}
    options={{ ...CHART_DEFAULTS, plugins: { ...CHART_DEFAULTS.plugins, tooltip: { callbacks: { label: (c) => ` $${c.parsed.y.toFixed(2)}` } } } }}
  />
              </ChartCard>
              <ChartCard title={t("adm_plan_dist")} icon="orders" height={200}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12 }}>
                  <div style={{ height: 130, width: 130, position: "relative" }}>
                    <Doughnut
    data={{
      labels: [t("freePlan"), i18n.language === "ar" ? "\u0645\u062F\u0641\u0648\u0639" : "Paid"],
      datasets: [{
        data: [planFree, planPaid],
        backgroundColor: [T.border2, T.accent],
        borderWidth: 0,
        hoverOffset: 4
      }]
    }}
    options={{ ...CHART_DEFAULTS, cutout: "65%", plugins: { legend: { display: false } } }}
  />
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: T.text1, fontFamily: "inherit" }}>{planPaid}</span>
                      <span style={{ fontSize: 9, color: T.accent, fontWeight: 700 }}>{isAr ? "\u0645\u062F\u0641\u0648\u0639" : "PAID"}</span>
                    </div>
                  </div>
                  <ChartLegend items={[{ color: T.accent, label: `${isAr ? "\u0645\u062F\u0641\u0648\u0639" : "Paid"} (${planPaid})` }, { color: T.border2, label: `${t("freePlan")} (${planFree})` }]} />
                </div>
              </ChartCard>
            </div>
          </>}
        {}
        {tab === "users" && <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text1, margin: 0 }}>{t("adm_users")}</h2>
                <p style={{ fontSize: 13, color: T.text3, margin: "4px 0 0" }}>{fmt(totalUsers)} {t("adm_total_users").toLowerCase()}</p>
              </div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.text3, display: "flex" }}><Icon name="search" size={15} /></span>
                <input
    type="text"
    placeholder={t("adm_search_users")}
    value={search}
    onChange={(e) => {
      setSearch(e.target.value);
      setUserPage(1);
    }}
    style={{ paddingLeft: 34, paddingRight: 14, paddingTop: 9, paddingBottom: 9, background: T.bgCard, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text1, fontFamily: "inherit", fontSize: 13, width: 250, outline: "none" }}
  />
              </div>
            </div>
            <DataTable headers={[t("adm_users"), t("email"), t("adm_provider"), t("currentPlan"), t("adm_date"), t("adm_role"), t("adm_status"), t("adm_actions")]}>
              {users.map((u, i) => <TR key={u._id} i={i}>
                  <TD><div style={{ display: "flex", alignItems: "center", gap: 9 }}><Avatar src={u.avatar} name={u.name} /><span style={{ color: T.text1, fontWeight: 500 }}>{u.name}</span></div></TD>
                  <TD style={{ color: T.text2 }}>{u.email}</TD>
                  <TD><Badge color={u.authProvider === "google" ? "#4285F4" : T.orange}>{u.authProvider}</Badge></TD>
                  <TD><Badge color={u.plan?.type === "hourly" ? T.purple : T.accent}>{t(u.plan?.type === "hourly" ? "hourlyPlan" : u.plan?.type === "pro" ? "proPlan" : "freePlan")}</Badge></TD>
                  <TD style={{ color: T.text3, fontSize: 12 }}>{fmtD(u.createdAt)}</TD>
                  <TD><Badge color={u.role === "admin" ? T.amber : T.accent}>{u.role === "admin" ? isAr ? "\u0645\u062F\u064A\u0631" : "Admin" : isAr ? "\u0639\u0636\u0648" : "User"}</Badge></TD>
                  <TD><Badge color={u.banned ? T.red : "#22c55e"}>{u.banned ? t("adm_banned") : t("adm_active")}</Badge></TD>
                  <TD>
                    <div style={{ display: "flex", gap: 5 }}>
                      <Btn color={T.blue} border={`${T.blue}44`} onClick={() => openManage(u._id)}>
                        {isAr ? "إدارة" : "Manage"}
                      </Btn>
                      <Btn color={u.banned ? "#22c55e" : T.orange} border={u.banned ? "#22c55e44" : `${T.orange}44`} disabled={actionLoading === u._id} onClick={() => handleBan(u._id, !u.banned)}>
                        {u.banned ? t("adm_unban") : t("adm_ban")}
                      </Btn>
                      <Btn color={T.red} border={`${T.red}44`} disabled={actionLoading === u._id} onClick={() => handleDelete(u._id)}>
                        {t("adm_delete")}
                      </Btn>
                      <Btn color={T.amber} border={`${T.amber}44`} disabled={actionLoading === u._id} onClick={() => handleMakeAdmin(u._id, u.role)}>
                        {u.role === "admin" ? `\u2605 ${t("adm_remove_admin")}` : `\u2606 ${t("adm_make_admin")}`}
                      </Btn>
                    </div>
                  </TD>
                </TR>)}
            </DataTable>
            {userPages > 1 && <div style={{ display: "flex", justifyContent: "center", gap: 6, margin: "20px 0 32px" }}>
                <Btn onClick={() => setUserPage((p) => Math.max(1, p - 1))} disabled={userPage === 1}>←</Btn>
                {Array.from({ length: userPages }, (_, i) => i + 1).map((p) => <button key={p} onClick={() => setUserPage(p)} style={{
    width: 34,
    height: 34,
    borderRadius: 8,
    border: `1px solid ${p === userPage ? T.accent : T.border2}`,
    cursor: "pointer",
    fontFamily: "Outfit,sans-serif",
    fontSize: 13,
    background: p === userPage ? T.accent : T.bgCard,
    color: p === userPage ? "#fff" : T.text2
  }}>{p}</button>)}
                <Btn onClick={() => setUserPage((p) => Math.min(userPages, p + 1))} disabled={userPage === userPages}>→</Btn>
              </div>}
          </>}
        {}
        {tab === "orders" && <>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text1, margin: 0 }}>{t("adm_orders")}</h2>
              <p style={{ fontSize: 13, color: T.text3, margin: "4px 0 0" }}>{fmt(orders.length)} {t("adm_recent_tx")}</p>
            </div>
            {}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginBottom: 20 }}>
              <KpiCard icon="money" label={isAr ? "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A" : "Total Revenue"} value={fmtM(stats?.revenue?.total)} color={T.accent} />
              <KpiCard icon="orders" label={isAr ? "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0637\u0644\u0628\u0627\u062A" : "Total Orders"} value={fmt(stats?.orders?.total)} color={T.purple} />
              <KpiCard icon="bolt" label={isAr ? "\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u0633\u0627\u0639\u0629" : "Hourly Orders"} value={fmt(stats?.orders?.hourly)} color={T.blue} />
              <KpiCard icon="crown" label={isAr ? "\u0637\u0644\u0628\u0627\u062A \u0628\u0627\u0642\u0629 Pro" : "Monthly Orders"} value={fmt(stats?.orders?.monthly)} color="#34d399" />
            </div>
            <DataTable headers={[t("adm_users"), t("email"), t("adm_amount"), t("adm_currency"), t("currentPlan"), t("adm_date"), t("adm_status"), t("adm_actions")]}>
              {orders.map((o, i) => {
    const statusColor = o.status === "completed" ? "#22c55e" : o.status === "refunded" ? T.blue : o.status === "pending" ? T.amber : T.red;
    return <TR key={o._id} i={i}>
                  <TD><div style={{ display: "flex", alignItems: "center", gap: 9 }}><Avatar src={o.userId?.avatar} name={o.userId?.name} /><span style={{ color: T.text1 }}>{o.userId?.name || "Deleted"}</span></div></TD>
                  <TD style={{ color: T.text2 }}>{o.userId?.email || "\u2014"}</TD>
                  <TD style={{ color: "#22c55e", fontWeight: 700, fontSize: 14 }}>{fmtMoney(o.amount, o.currency)}</TD>
                  <TD style={{ color: T.text2, fontSize: 12 }}>{o.currency || "USD"}</TD>
                  <TD><Badge color={T.purple}>{t(o.planType === "hourly" ? "hourlyPlan" : o.planType === "pro" ? "proPlan" : "freePlan")}</Badge></TD>
                  <TD style={{ color: T.text3, fontSize: 12 }}>{fmtT(o.createdAt)}</TD>
                  <TD><Badge color={statusColor}>{o.status || "pending"}</Badge></TD>
                  <TD>
                    <div style={{ display: "flex", gap: 5 }}>
                      {o.status !== "completed" && <Btn color="#22c55e" border="#22c55e44" onClick={() => setOrderStatus(o.orderId, "completed")}>{isAr ? "\u062a\u0623\u0643\u064a\u062f" : "Complete"}</Btn>}
                      {o.status === "completed" && <Btn color={T.blue} border={`${T.blue}44`} onClick={() => setOrderStatus(o.orderId, "refunded")}>{isAr ? "\u0627\u0633\u062a\u0631\u062f\u0627\u062f" : "Refund"}</Btn>}
                      {o.status !== "failed" && <Btn color={T.red} border={`${T.red}44`} onClick={() => setOrderStatus(o.orderId, "failed")}>{isAr ? "\u0641\u0634\u0644" : "Fail"}</Btn>}
                    </div>
                  </TD>
                </TR>;
  })}
            </DataTable>
          </>}
        {}
        {tab === "sessions" && <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text1, margin: 0 }}>{t("adm_sessions")}</h2>
                <p style={{ fontSize: 13, color: T.text3, margin: "4px 0 0" }}>{sessions.length} {isAr ? "\u0645\u0633\u062A\u062E\u062F\u0645\u064A\u0646 \u0644\u062F\u064A\u0647\u0645 \u062C\u0644\u0633\u0627\u062A \u0646\u0634\u0637\u0629" : "users with active hourly plans"}</p>
              </div>
              <button onClick={loadSessions} style={{ padding: "9px 16px", background: T.bgCard, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text2, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
                {t("adm_refresh")}
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12, paddingBottom: 32 }}>
              {sessions.map((s) => {
    const remaining = Math.max(0, new Date(s.plan.endTime) -  new Date());
    const mins = Math.floor(remaining / 6e4);
    const secs = Math.floor(remaining % 6e4 / 1e3);
    const pct = remaining / 36e5 * 100;
    const urgent = mins < 10;
    return <div key={s._id} style={{ background: T.bgCard, border: `1px solid ${urgent ? T.orange + "44" : T.border}`, borderRadius: 16, padding: "18px 20px", transition: "border-color .3s" }}>
                    {urgent && <div style={{ fontSize: 10, fontWeight: 700, color: T.orange, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8, fontFamily: "Outfit,sans-serif" }}>Expiring soon</div>}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                      <Avatar src={s.avatar} name={s.name} size={38} />
                      <div>
                        <div style={{ fontWeight: 600, color: T.text1, fontSize: 14 }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: T.text3 }}>{s.email}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.text2, marginBottom: 8 }}>
                      <span>{t("adm_time_remaining")}</span>
                      <span style={{ color: urgent ? T.orange : T.accent, fontWeight: 700, fontFamily: "inherit", fontSize: 13 }}>
                        {isAr ? `${secs}\u062B ${mins}\u062F` : `${mins}m ${secs}s`}
                      </span>
                    </div>
                    <div style={{ height: 5, background: T.bg, borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ height: "100%", background: urgent ? T.orange : T.accent, width: `${pct}%`, borderRadius: 99, transition: "width 1s linear" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.text3, marginTop: 6 }}>
                      <span>{t("adm_started")}: {fmtT(s.plan?.startTime)}</span>
                      <span>{Math.round(pct)}%</span>
                    </div>
                  </div>;
  })}
              {sessions.length === 0 && <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 64, color: T.text3 }}>
                  <div style={{ marginBottom: 12, color: "var(--text-3)", display: "flex", justifyContent: "center" }}><Icon name="empty" size={44} /></div>
                  <p style={{ fontFamily: "inherit" }}>{t("adm_no_active_sessions")}</p>
                </div>}
            </div>
          </>}
        {}
        {tab === "tokens" && <>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text1, margin: 0 }}>{t("adm_token_usage")}</h2>
              <p style={{ fontSize: 13, color: T.text3, margin: "4px 0 0" }}>{t("adm_cost_analysis")}</p>
            </div>
            <div className="adm-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12, marginBottom: 20 }}>
              <KpiCard icon="tokens" label={t("adm_all_time_tokens")} value={fmtK(stats?.tokens?.total)} color={T.blue} />
              <KpiCard icon="sunrise" label={isAr ? "\u062A\u0648\u0643\u0646\u0632 \u0627\u0644\u064A\u0648\u0645" : "Tokens Today"} value={fmtK(stats?.tokens?.today)} color={T.purple} />
              <KpiCard icon="bulb" label={t("adm_est_daily_cost")} value={`$${((stats?.tokens?.total || 0) * 15e-8).toFixed(2)}`} color={T.amber} />
              <KpiCard icon="chart" label={isAr ? "\u0645\u062A\u0648\u0633\u0637 \u0627\u0644\u062C\u0644\u0633\u0629" : "Avg per Session"} value={fmtK(stats?.chats?.total ? Math.round((stats?.tokens?.total || 0) / stats.chats.total) : 0)} color={T.accent} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <ChartCard title={isAr ? "\u0627\u0644\u0627\u0633\u062A\u0647\u0644\u0627\u0643 \u0627\u0644\u064A\u0648\u0645\u064A \u0644\u0644\u062A\u0648\u0643\u0646\u0632" : "Daily Token Consumption"} icon="trendUp" height={220}>
                <Line
    data={{
      labels: dailyLabels,
      datasets: [{
        label: isAr ? "\u062A\u0648\u0643\u0646\u0632" : "Tokens",
        data: tokenData,
        borderColor: T.blue,
        backgroundColor: "rgba(56,189,248,.08)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: T.blue,
        borderWidth: 2
      }]
    }}
    options={{ ...CHART_DEFAULTS, plugins: { ...CHART_DEFAULTS.plugins, tooltip: { callbacks: { label: (c) => ` ${fmtK(c.parsed.y)} ${isAr ? "\u062A\u0648\u0643\u0646\u0632" : "tokens"}` } } } }}
  />
              </ChartCard>
              <ChartCard title={t("adm_est_daily_cost")} icon="money" height={220}>
                <Bar
    data={{
      labels: dailyLabels,
      datasets: [{
        label: t("adm_est_cost"),
        data: tokenData.map((t2) => +((t2 || 0) * 15e-8).toFixed(4)),
        backgroundColor: "rgba(251,191,36,.4)",
        borderColor: T.amber,
        borderWidth: 1.5,
        borderRadius: 5
      }]
    }}
    options={{ ...CHART_DEFAULTS, plugins: { ...CHART_DEFAULTS.plugins, tooltip: { callbacks: { label: (c) => ` $${c.parsed.y.toFixed(4)}` } } } }}
  />
              </ChartCard>
            </div>
            <SectionTitle icon="trophy">{t("adm_top_token_consumers")}</SectionTitle>
            <DataTable headers={["#", t("adm_users"), t("email"), t("currentPlan"), t("adm_total_tokens"), t("adm_est_cost")]}>
              {(stats?.topTokenUsers || []).map((u, i) => <TR key={u._id} i={i}>
                  <TD style={{ color: T.text3, fontWeight: 700, width: 40 }}>#{i + 1}</TD>
                  <TD><div style={{ display: "flex", alignItems: "center", gap: 9 }}><Avatar src={u.avatar} name={u.name} /><span style={{ color: T.text1, fontWeight: 500 }}>{u.name}</span></div></TD>
                  <TD style={{ color: T.text2 }}>{u.email}</TD>
                  <TD><Badge color={T.purple}>{t(u.plan?.type === "hourly" ? "hourlyPlan" : u.plan?.type === "pro" ? "proPlan" : "freePlan")}</Badge></TD>
                  <TD style={{ color: T.blue, fontWeight: 700 }}>{fmtK(u.totalTokensUsed)}</TD>
                  <TD style={{ color: T.amber }}>${((u.totalTokensUsed || 0) * 15e-8).toFixed(4)}</TD>
                </TR>)}
            </DataTable>
          </>}
        {}
        {tab === "deletions" && <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text1, margin: 0 }}>{t("adm_deletion_requests")}</h2>
                <p style={{ fontSize: 13, color: T.text3, margin: "4px 0 0" }}>{deletionRequests.length} {t("adm_deletion_requests").toLowerCase()}</p>
              </div>
              <Btn onClick={loadDeletionRequests}>{t("adm_refresh")}</Btn>
            </div>
            {deletionRequests.length === 0 ? <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, padding: 48, textAlign: "center", color: T.text3 }}>
                <div style={{ marginBottom: 10, color: "var(--text-3)", display: "flex", justifyContent: "center" }}><Icon name="deletions" size={40} /></div>
                <p style={{ fontFamily: "inherit", margin: 0 }}>{t("adm_deletion_no_requests")}</p>
              </div> : <DataTable headers={[t("adm_users"), t("email"), t("adm_provider"), t("currentPlan"), t("adm_deletion_requested_at"), t("adm_deletion_reason"), t("adm_actions")]}>
                {deletionRequests.map((u, i) => <TR key={u._id} i={i}>
                    <TD><div style={{ display: "flex", alignItems: "center", gap: 9 }}><Avatar src={u.avatar} name={u.name} /><span style={{ color: T.text1, fontWeight: 500 }}>{u.name}</span></div></TD>
                    <TD style={{ color: T.text2 }}>{u.email}</TD>
                    <TD><Badge color={u.authProvider === "google" ? "#4285F4" : T.orange}>{u.authProvider}</Badge></TD>
                    <TD><Badge color={u.plan?.type === "hourly" ? T.purple : T.accent}>{t(u.plan?.type === "hourly" ? "hourlyPlan" : u.plan?.type === "monthly" ? "proPlan" : "freePlan")}</Badge></TD>
                    <TD style={{ color: T.text3, fontSize: 12 }}>{u.deletionRequest?.requestedAt ? fmtT(u.deletionRequest.requestedAt) : "\u2014"}</TD>
                    <TD style={{ color: T.text2, fontSize: 12, maxWidth: 240, whiteSpace: "normal" }}>{u.deletionRequest?.reason || "\u2014"}</TD>
                    <TD>
                      <div style={{ display: "flex", gap: 5 }}>
                        <Btn color={T.red} border={`${T.red}55`} disabled={deletionActionLoading === u._id} onClick={() => approveDeletion(u._id)}>
                          {t("adm_deletion_approve")}
                        </Btn>
                        <Btn color={T.text2} border={T.border2} disabled={deletionActionLoading === u._id} onClick={() => rejectDeletion(u._id)}>
                          {t("adm_deletion_reject")}
                        </Btn>
                      </div>
                    </TD>
                  </TR>)}
              </DataTable>}
          </>}
        {tab === "openai" && <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text1, margin: 0 }}>{t("adm_openai")}</h2>
                <p style={{ fontSize: 13, color: T.text3, margin: "4px 0 0" }}>{t("adm_openai_budget")}</p>
              </div>
              <Btn color={T.accent} border={`${T.accent}44`} onClick={() => window.open(openaiUsage?.billingUrl || "https://platform.openai.com/settings/organization/billing/overview", "_blank")}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="card" size={15} /> {isAr ? "شحن الرصيد" : "Add credits"}</span>
              </Btn>
            </div>
            {openaiUsage?.warning && <div style={{ background: `${T.red}15`, border: `1px solid ${T.red}44`, color: T.red, padding: "12px 16px", borderRadius: 12, marginBottom: 20, display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 600 }}>
                <span style={{ display: "flex" }}><Icon name="warning" size={16} /></span> {openaiUsage.warning}
              </div>}
            {openaiUsage && openaiUsage.available === false && <div style={{ background: `${T.amber}12`, border: `1px solid ${T.amber}44`, color: T.text2, padding: "12px 16px", borderRadius: 12, marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
                <b style={{ color: T.amber }}>{isAr ? "التكلفة الحقيقية من OpenAI غير متاحة" : "Live OpenAI spend unavailable"}</b><br />
                {isAr ? "لعرض الإنفاق الحقيقي من OpenAI، أضف مفتاح إدارة (sk-admin-...) في متغير OPENAI_ADMIN_KEY على الخادم. الأرقام أدناه محسوبة من استهلاك التوكنز المسجّل في قاعدة البيانات." : "To show real OpenAI spend, set an admin key (sk-admin-...) as OPENAI_ADMIN_KEY on the server. The figures below are estimated from the tokens tracked in your database."}
              </div>}
            <SectionTitle icon="chart">{isAr ? "الإنفاق والاستهلاك" : "Spend & usage"}</SectionTitle>
            <div className="adm-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12, marginBottom: 32 }}>
              <KpiCard icon="money" label={isAr ? "إنفاق OpenAI هذا الشهر" : "OpenAI spend (this month)"} value={openaiUsage?.available ? `$${(openaiUsage?.total_cost || 0).toFixed(2)}` : "—"} color={T.amber} />
              <KpiCard icon="chart" label={isAr ? "تكلفة تقديرية (من التوكنز)" : "Estimated cost (from tokens)"} value={`$${((stats?.tokens?.total || 0) * 15e-8).toFixed(2)}`} color={T.red} />
              <KpiCard icon="tokens" label={isAr ? "إجمالي التوكنز" : "Total tokens"} value={fmtK(stats?.tokens?.total)} color={T.blue} />
              <KpiCard icon="sunrise" label={isAr ? "توكنز اليوم" : "Tokens today"} value={fmtK(stats?.tokens?.today)} color={T.purple} />
            </div>
            <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20, fontSize: 12, color: T.text3, lineHeight: 1.7 }}>
              {isAr
                ? "ملاحظة: OpenAI لا توفّر واجهة لعرض الرصيد المتبقّي من الكريدت. نعرض الإنفاق الفعلي (عند ضبط مفتاح الإدارة) والتقدير المحسوب محليًا من التوكنز، مع زر مباشر لشحن الرصيد."
                : "Note: OpenAI provides no API for remaining prepaid credit balance. We show real spend (when an admin key is set) plus a locally-computed estimate from tokens, with a direct button to add credits."}
            </div>
          </>}
      </main>
      {manageUser && <UserManageModal
        data={manageUser}
        loading={manageLoading}
        isAr={isAr}
        T={T}
        onClose={() => setManageUser(null)}
        onSavePlan={saveUserPlan}
        fmtT={fmtT}
        fmtMoney={fmtMoney}
      />}
    </div>;
};

// --- User management modal: profile, orders, chats + change plan / reset msgs ---
const UserManageModal = ({ data, loading, isAr, T, onClose, onSavePlan, fmtT, fmtMoney }) => {
  const u = data?.user;
  const [planType, setPlanType] = React.useState(u?.plan?.type || "free");
  const [days, setDays] = React.useState(30);
  const [hours, setHours] = React.useState(1);
  React.useEffect(() => { if (u?.plan?.type) setPlanType(u.plan.type); }, [u?.plan?.type]);
  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const card = { background: T.bg, border: `1px solid ${T.border}`, borderRadius: 18, width: "min(680px,96vw)", maxHeight: "90vh", overflowY: "auto", padding: 24 };
  const lbl = { fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: .5 };
  const input = { background: T.bgCard, border: `1px solid ${T.border2}`, borderRadius: 8, color: T.text1, padding: "8px 10px", fontFamily: "inherit", fontSize: 13, width: 90 };
  return <div style={overlay} onClick={onClose}>
    <div className="adm-modal-card" style={card} onClick={(e) => e.stopPropagation()}>
      {(!u || data.loading) ? <div style={{ padding: 40, textAlign: "center", color: T.text3 }}>{isAr ? "جارٍ التحميل..." : "Loading..."}</div> : <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text1 }}>{u.name}</div>
            <div style={{ fontSize: 13, color: T.text3 }}>{u.email}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.text3, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
          <Stat T={T} label={isAr ? "الخطة" : "Plan"} value={u.plan?.type || "free"} />
          <Stat T={T} label={isAr ? "الرسائل المجانية المستخدمة" : "Free msgs used"} value={`${u.messageCount || 0}/10`} />
          <Stat T={T} label={isAr ? "التوكنز" : "Tokens"} value={(u.totalTokensUsed || 0).toLocaleString()} />
          <Stat T={T} label={isAr ? "المحادثات" : "Messages"} value={data.stats?.totalMessages || 0} />
          <Stat T={T} label={isAr ? "الجلسات" : "Sessions"} value={data.stats?.totalSessions || 0} />
          <Stat T={T} label={isAr ? "الطلبات" : "Orders"} value={data.orders?.length || 0} />
        </div>
        <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ ...lbl, marginBottom: 10 }}>{isAr ? "تعديل الخطة يدويًا" : "Change plan manually"}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <select value={planType} onChange={(e) => setPlanType(e.target.value)} style={{ ...input, width: 120 }}>
              <option value="free">Free</option>
              <option value="hourly">Hourly</option>
              <option value="monthly">Monthly</option>
            </select>
            {planType === "monthly" && <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}>{isAr ? "أيام" : "Days"}<input type="number" value={days} min={1} onChange={(e) => setDays(e.target.value)} style={input} /></label>}
            {planType === "hourly" && <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}>{isAr ? "ساعات" : "Hours"}<input type="number" value={hours} min={1} onChange={(e) => setHours(e.target.value)} style={input} /></label>}
            <Btn color={T.accent} border={`${T.accent}66`} disabled={loading} onClick={() => onSavePlan(u._id, { planType, days: Number(days), hours: Number(hours) })}>{isAr ? "حفظ الخطة" : "Save plan"}</Btn>
            <Btn color={T.blue} border={`${T.blue}44`} disabled={loading} onClick={() => onSavePlan(u._id, { resetMessages: true })}>{isAr ? "تصفير الرسائل المجانية" : "Reset free msgs"}</Btn>
          </div>
        </div>
        {data.orders?.length > 0 && <div style={{ marginBottom: 16 }}>
          <div style={{ ...lbl, marginBottom: 8 }}>{isAr ? "الطلبات" : "Orders"}</div>
          {data.orders.slice(0, 8).map((o) => <div key={o._id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.text2, padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
            <span>{o.orderId}</span><span>{fmtMoney(o.amount, o.currency)}</span><span style={{ color: o.status === "completed" ? "#22c55e" : T.amber }}>{o.status}</span><span style={{ color: T.text3 }}>{fmtT(o.createdAt)}</span>
          </div>)}
        </div>}
        {data.recentChats?.length > 0 && <div>
          <div style={{ ...lbl, marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{isAr ? "سجل المحادثة" : "Conversation transcript"}</span>
            <span style={{ fontWeight: 500, color: T.text3, textTransform: "none", letterSpacing: 0 }}>{isAr ? `آخر ${data.recentChats.length} رسالة` : `last ${data.recentChats.length} messages`}</span>
          </div>
          <div style={{ maxHeight: 340, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, padding: "12px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12 }}>
            {[...data.recentChats].reverse().map((c) => {
              const isAssistant = c.role === "assistant";
              return <div key={c._id} style={{ display: "flex", flexDirection: "column", alignItems: isAssistant ? "flex-start" : "flex-end" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3, color: T.text3, fontSize: 10 }}>
                  <span style={{ color: isAssistant ? T.accent : T.blue, display: "flex" }}><Icon name={isAssistant ? "robot" : "user"} size={12} /></span>
                  <span>{isAssistant ? (isAr ? "المساعد" : "Assistant") : (isAr ? "المستخدم" : "User")}</span>
                  {c.createdAt && <span>· {fmtT(c.createdAt)}</span>}
                </div>
                <div dir="auto" style={{
                  maxWidth: "82%", whiteSpace: "pre-wrap", wordBreak: "break-word",
                  background: isAssistant ? T.bgCard : `${T.blue}18`,
                  border: `1px solid ${isAssistant ? T.border : T.blue + "33"}`,
                  color: T.text1, borderRadius: 12, padding: "9px 12px", fontSize: 13, lineHeight: 1.7
                }}>{c.content || ""}</div>
              </div>;
            })}
          </div>
        </div>}
      </>}
    </div>
  </div>;
};
const Stat = ({ T, label, value }) => <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px" }}>
  <div style={{ fontSize: 10, color: T.text3, textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
  <div style={{ fontSize: 16, fontWeight: 800, color: T.text1, marginTop: 2 }}>{value}</div>
</div>;
var stdin_default = AdminDashboard;
export {
  stdin_default as default
};
